/**
 * Texts for the article restrictions (OBR-135): why a field is locked, what to do instead, an administrator’s controlled
 * correction, and the report of the bulk edit and of the importer.
 * The locks and warnings are decided by `articleEditPolicy` (@obrava/shared); only the wording lives here.
 */
export const articleRestrictionsEn = {
  'backoffice.articleRestrictions': {
    // ── Usage state (the banner on the article card) ───────────────────────────────────────────
    usageBannerE1:
      'This article is already in recipes, orders, templates or other documents: some fields can no longer be changed.',
    usageBannerE2:
      'This article already has history (movements, lots, weighings, receptions or shipments, even if it has no stock now): some fields can no longer be changed.',
    usageBannerE3:
      'This article has stock and history: some fields can no longer be changed.',

    // ── Why a field is locked ──────────────────────────────────────────────────────────────────
    reasonSkuReferenced:
      'The code cannot be changed: the article is already in recipes, orders, templates or other documents, which would keep showing the old code.',
    reasonSkuHistory:
      'The code cannot be changed: the article already has history (movements, lots, receptions…), even if it has no stock now.',
    reasonBaseUnitReferenced:
      'The base unit cannot be changed: the article is already in recipes, orders or other documents, which would stay in the old unit.',
    reasonBaseUnitHistory:
      'The base unit cannot be changed: the stock and the history are already counted in this unit.',
    reasonRecipeUnitRecipeLines:
      'The recipe unit cannot be changed or removed: published recipes use it.',
    reasonRecipeUnitMovements:
      'The recipe unit cannot be changed or removed: movements or weighings are already recorded in this unit.',
    reasonRecipeFactorRecipeLines:
      'The equivalence cannot be changed: published recipes use this unit.',
    reasonRecipeFactorMovements:
      'The equivalence cannot be changed: movements or weighings are already recorded in this unit.',
    reasonTypeIncompatible:
      'The type cannot be changed: this change alters how the article is bought or made, and it has already been used.',
    reasonTypePublishedRecipe:
      'It cannot switch between raw material and auxiliary product: the article is an ingredient of a published recipe.',
    typeUnavailable:
      'Some types cannot be chosen: the article has already been used and can only switch to a compatible one (semi-finished ↔ finished product, raw material ↔ auxiliary product).',

    // ── Warnings (they do not block: they ask for confirmation when saving) ────────────────────
    warnRecipeUnit:
      'This article is already in documents or has history. Changing or removing the recipe unit may affect draft recipes.',
    warnArticleType:
      'This article has already been used. Switching between raw material and auxiliary product changes how it is declared on the sheet and the label.',
    pendingBlockedIntro: 'This save cannot be done:',
    warnNeedsConfirm: 'You will have to confirm it when saving.',
    confirmWarningsTitle: 'Confirm the change',
    confirmWarningsIntro: 'This save includes changes to an article that has already been used:',
    confirmWarningsConfirm: 'Save anyway',

    // ── What to do instead ─────────────────────────────────────────────────────────────────────
    alternativeNewArticle: 'If needed, create a new article and deactivate the old one.',
    alternativeCorrection: 'An administrator can make a controlled correction, with a reason.',

    // ── Controlled correction (administrator) ──────────────────────────────────────────────────
    correctionAction: 'Controlled correction',
    correctionTitleSku: 'Controlled correction of the code',
    correctionTitleType: 'Controlled correction of the type',
    correctionIntro:
      'Only to fix a mistake. It is saved in the activity log with the reason and who made it. Existing documents and history are not modified.',
    correctionSkuLabel: 'New code',
    correctionTypeLabel: 'New type',
    correctionReasonLabel: 'Reason (required)',
    correctionReasonHint: 'Why does it need correcting?',
    correctionConfirm: 'Correct',
    correctionDone: 'Correction made and recorded.',
    correctionUnsavedHint:
      'Save the changes on the card first: the correction happens immediately and would reload them.',
    correctionErrorForbidden: 'Only an administrator can make a controlled correction.',
    correctionErrorReasonRequired: 'Write the reason.',
    correctionErrorValueRequired: 'Write the new value.',
    correctionErrorValueInvalid: 'The value is not valid.',
    correctionErrorInUse:
      'The type of this article cannot be corrected: it is the output of a recipe or has finished-product lots or production orders. Create a new article and deactivate the old one.',
    correctionErrorSkuInvalid:
      'The code is not valid: at most 64 characters, on a single line, with no tabs or spaces at the start or end.',
    correctionTypeInUse:
      'The type of this article cannot be corrected: it is the output of a recipe or has finished-product lots or production orders.',
    correctionErrorUnchanged: 'The new value is the same as the current one.',
    correctionErrorRecipeUnit:
      'Remove the recipe unit first: only a raw material can have one.',
    correctionErrorNotFound: 'The article was not found (it may have been deactivated).',
    correctionErrorDuplicate: 'Another article already has this code.',
    correctionErrorGeneric: 'The correction could not be made.',

    // ── The server rejected it (someone used the article while you were editing) ───────────────
    serverLocked:
      'The server did not allow the change to “{{field}}”: the article has already been used. Reload the card to see its state.',
    fieldSku: 'Code',
    fieldBaseUnit: 'Base unit',
    fieldRecipeUnit: 'Recipe unit',
    fieldRecipeUnitFactor: 'Recipe unit equivalence',
    fieldArticleType: 'Type',

    // ── Bulk edit ──────────────────────────────────────────────────────────────────────────────
    bulkPreview_one:
      '{{count}} of the selected articles cannot be changed because it has already been used. It will be detailed when done; the rest will be updated.',
    bulkPreview_other:
      '{{count}} of the selected articles cannot be changed because they have already been used. They will be detailed when done; the rest will be updated.',
    bulkPreviewLoading: 'Checking which articles can be changed…',
    bulkReportTitle: 'Bulk edit result',
    bulkReportSummary_one: '{{done}} of {{total}} article updated; {{count}} could not be changed.',
    bulkReportSummary_other:
      '{{done}} of {{total}} articles updated; {{count}} could not be changed.',
    bulkReportBlockedHeading: 'Not changed',
    bulkReportBlockedNote:
      'These articles have already been used and the change is not allowed. The rest were updated; no other article was touched.',
    bulkReportNothingDone: 'No article could be changed.',
    bulkReportClose: 'Close',

    // ── Importer ───────────────────────────────────────────────────────────────────────────────
    importRowBlocked: 'This row will not be imported: {{reason}}',
    importRowWarning: 'Warning: {{reason}}',
  },
} as const;
